<template>
  <div v-if="telemetryFactText" class="evidence-box evidence-telemetry mb-3">
    <div class="evidence-box-title mb-2">
      <div class="title-left-wrap">
        <v-icon size="16" color="#2563eb" class="mr-1">mdi-timer-alert-outline</v-icon>
        <span class="section-sub-title">数据连续性与中断核验</span>
      </div>
      <!-- 高层级核验定性：克制优雅的纯净状态排版（状态图标 + 加粗定性 + 灰色事实支撑），无刺眼大色块与多余边框 -->
      <div v-if="gapsList.length" class="telemetry-headline-status">
        <v-icon size="15" color="#dc2626" class="mr-1">mdi-alert-circle</v-icon>
        <span class="status-title text-danger">数据已中断</span>
        <!-- <span class="status-divider">/</span>
        <span class="status-meta">末次记录 {{ lastRecordTimeText }}，共 {{ records.length }} 点</span> -->
      </div>
      <div v-else-if="records.length" class="telemetry-headline-status">
        <v-icon size="15" color="#16a34a" class="mr-1">mdi-check-circle</v-icon>
        <span class="status-title text-success">数据连续</span>
        <span class="status-divider">/</span>
        <span class="status-meta">共 {{ records.length }} 条连续采样</span>
      </div>
    </div>

    <!-- 结论文字陈述（断档区间、持续分钟、前后SOC） -->
    <p class="evidence-box-desc pl-5">
      {{ telemetryFactText }}
    </p>

    <!-- 时序曲线图表容器 -->
    <div class="telemetry-chart-wrapper mt-2 ml-5">
      <!-- 图表专属时序指标栏：整条轻柔浅灰背景，纯净键值对排版，无任何碎红碎蓝边框 -->
      <div v-if="gapsList.length" class="telemetry-metric-ribbon mb-2">
        <div class="ribbon-item">
          <span class="ribbon-label">数据中断统计</span>
          <span class="ribbon-val">共 {{ gapsList.length }} 处（累计中断 {{ totalGapMinutes }} 分钟）</span>
        </div>
        <template v-for="g in gapsList" :key="g.gapIndex">
          <span class="ribbon-divider"></span>
          <div class="ribbon-item">
            <span class="ribbon-label">{{ g.isTailGap && g.isOngoing ? `中断时段${g.gapIndex} (持续 ${g.gapMinutes}m)` :
              `中断时段${g.gapIndex} (${g.gapMinutes}m)` }}</span>
            <span class="ribbon-val">{{ g.gapStart?.slice(11, 16) }} ~ {{ g.isTailGap && g.isOngoing ? '至今未恢复' :
              g.gapEnd?.slice(11, 16) }}</span>
            <span v-if="g.preSoc !== null" class="ribbon-sub">SOC: {{ g.preSoc }}%→{{ g.postSoc !== null && g.postSoc
              !== undefined ? g.postSoc + '%' : '未恢复' }}</span>
          </div>
        </template>
      </div>

      <div class="chart-top-legend">
        <template v-if="isMeterDevice">
          <div class="legend-indicator">
            <span class="legend-dot" style="background:#1D77F5;"></span>
            <span class="legend-label">上栏：有功功率 (kW)</span>
          </div>
          <div class="legend-indicator">
            <span class="legend-dot" style="background:#FFC859;"></span>
            <span class="legend-label">下栏：无功功率 (kvar)</span>
          </div>
          <div class="legend-indicator">
            <span class="legend-dot" style="background:#00D4AA;"></span>
            <span class="legend-label">下栏(右轴)：功率因数 (PF)</span>
          </div>
        </template>
        <template v-else>
          <div class="legend-indicator">
            <span class="legend-dot dot-soc"></span>
            <span class="legend-label">上栏：电池 SOC (%)</span>
          </div>
          <div class="legend-indicator">
            <span class="legend-dot dot-power"></span>
            <span class="legend-label">下栏：充放电功率 (kW)</span>
          </div>
        </template>
        <div class="legend-indicator">
          <span class="legend-bar bar-gap"></span>
          <span class="legend-label">红虚线区：数据中断时段</span>
        </div>
      </div>

      <div ref="chartDomRef" class="telemetry-chart-canvas"></div>

      <div class="chart-bottom-hint">
        <v-icon size="12" color="#8c8c8c" class="mr-1">mdi-information-outline</v-icon>
        <span v-if="isMeterDevice">数据来源：电表历史监测数据，红框与虚线标示数据中断时段。</span>
        <span v-else>数据来源：储能设备功率跟踪数据，红框与虚线标示数据中断时段。</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import dayjs from 'dayjs'
import * as echarts from 'echarts'

const props = defineProps({
  records: {
    type: Array,
    default: () => []
  },
  alarmRecord: {
    type: Object,
    default: () => ({})
  },
  rawFactText: {
    type: String,
    default: ''
  },
  powerTrackingStart: {
    type: String,
    default: ''
  },
  powerTrackingEnd: {
    type: String,
    default: ''
  }
})

const chartDomRef = ref(null)
let chartInstance = null

const isMeterDevice = computed(() => {
  const row = props.alarmRecord || {}
  const devName = (row.equipmentName || row.deviceName || '').toLowerCase()
  const devUuid = (row.uuid || row.deviceVid || '').toLowerCase()
  const rawList = Array.isArray(props.records) ? props.records : []
  const hasMeterField = rawList.some(it => it.is_meter || it.p_total !== undefined || it.pf !== undefined)
  return hasMeterField || row.type === 'METER' || devName.includes('电表') || devName.includes('meter') || devUuid.includes('pcc') || devUuid.includes('gcp')
})

const isStationOffline = computed(() => {
  const row = props.alarmRecord || {}
  const devName = (row.equipmentName || row.deviceName || '').toLowerCase()
  const code = String(row.alarmCode || row.code || '')
  return code === 'A0_00_04_0001' || devName.includes('整站') || devName.includes('储能电站')
})

const formattedData = computed(() => {
  const rawList = Array.isArray(props.records) ? props.records : []
  return rawList
    .map(item => {
      const timeStr = item.record_time || item.recordTime || item.time || item.collectTime || ''
      const socVal = item.batterysystemsoc ?? item.batterySystemSoc ?? item.soc
      const powerVal = item.p_total ?? item.pcspower_dc ?? item.pcsPowerDc ?? item.power ?? item.pcspower
      const qVal = item.q_total ?? item.qTotal
      const pfVal = item.pf ?? item.powerFactor
      const d = dayjs(timeStr)
      if (!timeStr || !d.isValid()) return null
      return {
        time: d.format('YYYY-MM-DD HH:mm:ss'),
        rawTime: d.valueOf(),
        soc: (socVal !== null && socVal !== undefined && !isNaN(Number(socVal))) ? Number(Number(socVal).toFixed(2)) : null,
        power: (powerVal !== null && powerVal !== undefined && !isNaN(Number(powerVal))) ? Number(Number(powerVal).toFixed(2)) : null,
        qTotal: (qVal !== null && qVal !== undefined && !isNaN(Number(qVal))) ? Number(Number(qVal).toFixed(2)) : null,
        pf: (pfVal !== null && pfVal !== undefined && !isNaN(Number(pfVal))) ? Number(Number(pfVal).toFixed(3)) : null,
        isMeter: !!item.is_meter || item.p_total !== undefined || isMeterDevice.value
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.rawTime - b.rawTime)
})

const lastRecordTimeText = computed(() => {
  const list = formattedData.value
  if (!list.length) return '--'
  return list[list.length - 1].time?.slice(11, 16) || '--'
})

const gapsList = computed(() => {
  const data = formattedData.value
  if (!data.length) return []

  const stepDiffs = []
  for (let i = 0; i < data.length - 1; i++) {
    const diffM = Math.round((data[i + 1].rawTime - data[i].rawTime) / (60 * 1000))
    if (diffM > 0) stepDiffs.push(diffM)
  }
  stepDiffs.sort((a, b) => a - b)
  const baseStep = stepDiffs.length ? stepDiffs[Math.floor(stepDiffs.length / 2)] : 15
  const gapThresholdMin = Math.max(Math.round(baseStep * 1.5), 25)

  const detected = []
  for (let i = 0; i < data.length - 1; i++) {
    const t1 = data[i].rawTime
    const t2 = data[i + 1].rawTime
    const diffMin = (t2 - t1) / (60 * 1000)
    if (diffMin >= gapThresholdMin) {
      detected.push({
        gapIndex: detected.length + 1,
        gapStart: data[i].time,
        gapEnd: data[i + 1].time,
        gapMinutes: Math.round(diffMin),
        preSoc: data[i].soc,
        postSoc: data[i + 1].soc,
        prePower: data[i].power,
        postPower: data[i + 1].power,
        isTailGap: false,
        isOngoing: false
      })
    }
  }

  // 关键末端断档核验：10:05 至今无数据上报
  const endTargetStr = props.powerTrackingEnd || (props.alarmRecord?.endTime && props.alarmRecord?.endTime !== '--' && props.alarmRecord?.endTime !== '/' ? dayjs(props.alarmRecord.endTime).format('YYYY-MM-DD HH:mm:ss') : dayjs().format('YYYY-MM-DD HH:mm:ss'))
  const endTargetMoment = dayjs(endTargetStr)
  if (data.length > 0 && endTargetMoment.isValid()) {
    const lastPoint = data[data.length - 1]
    const tailDiffMin = (endTargetMoment.valueOf() - lastPoint.rawTime) / (60 * 1000)
    if (tailDiffMin >= gapThresholdMin) {
      const isOngoing = !props.alarmRecord?.endTime || props.alarmRecord?.endTime === '--' || props.alarmRecord?.endTime === '/'
      detected.push({
        gapIndex: detected.length + 1,
        gapStart: lastPoint.time,
        gapEnd: endTargetStr,
        gapMinutes: Math.round(tailDiffMin),
        preSoc: lastPoint.soc,
        postSoc: null,
        prePower: lastPoint.power,
        postPower: null,
        isTailGap: true,
        isOngoing
      })
    }
  }

  return detected
})

const totalGapMinutes = computed(() => gapsList.value.reduce((acc, g) => acc + (g.gapMinutes || 0), 0))

const telemetryFactText = computed(() => {
  if (gapsList.value.length > 0) {
    const totalMin = totalGapMinutes.value
    const g = gapsList.value[0]
    const scopeLabel = isStationOffline.value ? '整站全域时序遥测' : (isMeterDevice.value ? '电表时序遥测' : '充放电时序遥测')

    if (gapsList.value.length === 1) {
      const endDesc = g.isTailGap && g.isOngoing ? '至今未恢复' : g.gapEnd
      if (isMeterDevice.value) {
        const powerPost = g.postPower !== null && g.postPower !== undefined ? `，恢复后有功功率: ${g.postPower} kW` : '，遥测未恢复'
        const preP = g.prePower !== null && g.prePower !== undefined ? `${g.prePower} kW` : '--'
        return `${scopeLabel}发生中断（自 ${g.gapStart} 至 ${endDesc}，持续中断 ${g.gapMinutes} 分钟）。中断前有功功率: ${preP}${powerPost}，表明电表通信链路中断。`
      }
      const socPost = g.postSoc !== null && g.postSoc !== undefined ? `，恢复后 SOC: ${g.postSoc}%` : '，遥测未恢复'
      return `${scopeLabel}发生中断（自 ${g.gapStart} 至 ${endDesc}，持续中断 ${g.gapMinutes} 分钟）。中断前 SOC: ${g.preSoc}%${socPost}，表明物理通信与遥测中断。`
    }
    const gapDetails = gapsList.value.map(item => {
      const endD = item.isTailGap && item.isOngoing ? '至今未恢复' : (item.gapEnd ? item.gapEnd.slice(11, 16) : '--')
      const startD = item.gapStart ? item.gapStart.slice(11, 16) : '--'
      return `第${item.gapIndex}段：${startD}~${endD}（${item.isTailGap && item.isOngoing ? '持续' : ''}中断${item.gapMinutes}分钟）`
    }).join('；')
    if (isMeterDevice.value) {
      const preP = g.prePower !== null && g.prePower !== undefined ? `${g.prePower} kW` : '--'
      return `核验发现该时段共发生 ${gapsList.value.length} 次遥测中断（累计中断 ${totalMin} 分钟）：${gapDetails}。中断前有功功率: ${preP}，表明该电表遥测失联。`
    }
    return `核验发现该时段共发生 ${gapsList.value.length} 次遥测中断（累计中断 ${totalMin} 分钟）：${gapDetails}。中断前 SOC: ${g.preSoc}%，表明该设备遥测失联。`
  }
  return props.rawFactText || (isMeterDevice.value ? '电表时序遥测连续上报，未发生数据中断。' : '充放电时序遥测连续上报，未发生数据中断。')
})

const alarmStartTimeText = computed(() => {
  const t = props.alarmRecord?.startTime || props.alarmRecord?.firstTriggerTime || props.alarmRecord?.firstTime || props.alarmRecord?.start_time || ''
  return t ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : ''
})

const alarmLastTimeText = computed(() => {
  const t = props.alarmRecord?.lastTime || props.alarmRecord?.lastTriggerTime || props.alarmRecord?.last_time || ''
  return t ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : ''
})

const alarmEndTimeText = computed(() => {
  const t = props.alarmRecord?.endTime || props.alarmRecord?.end_time || ''
  return t && t !== '--' && t !== '/' && dayjs(t).isValid() ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : ''
})

function renderChart() {
  const dom = chartDomRef.value
  if (!dom) return

  try {
    if (chartInstance) {
      chartInstance.dispose()
      chartInstance = null
    }

    const data = formattedData.value
    const gaps = gapsList.value

    const endTargetStr = props.powerTrackingEnd || (props.alarmRecord?.endTime && props.alarmRecord?.endTime !== '--' && props.alarmRecord?.endTime !== '/' ? dayjs(props.alarmRecord.endTime).format('YYYY-MM-DD HH:mm:ss') : dayjs().format('YYYY-MM-DD HH:mm:ss'))

    const markAreaUpper = []
    const markAreaLower = []
    gaps.forEach(g => {
      markAreaUpper.push([
        {
          name: `断档${g.gapIndex}`,
          xAxis: g.gapStart,
          itemStyle: {
            color: 'rgba(255, 77, 79, 0.16)',
            borderColor: 'rgba(255, 77, 79, 0.6)',
            borderWidth: 1,
            borderType: 'dashed'
          },
          label: {
            show: true,
            position: 'insideTop',
            formatter: g.isTailGap && g.isOngoing
              ? `断档${g.gapIndex} (持续中断 ${g.gapMinutes}m)\n${g.gapStart.slice(11, 16)}~至今(未恢复)`
              : `断档${g.gapIndex} (${g.gapMinutes}m)\n${g.gapStart.slice(11, 16)}~${g.gapEnd.slice(11, 16)}`,
            color: '#cf1322',
            fontSize: 10,
            fontWeight: 'bold',
            backgroundColor: 'rgba(255, 241, 240, 0.95)',
            borderColor: '#ffa39e',
            borderWidth: 1,
            borderRadius: 3,
            padding: [2, 4]
          }
        },
        { xAxis: g.gapEnd }
      ])
      markAreaLower.push([
        {
          xAxis: g.gapStart,
          itemStyle: {
            color: 'rgba(255, 77, 79, 0.12)',
            borderColor: 'rgba(255, 77, 79, 0.5)',
            borderWidth: 1,
            borderType: 'dashed'
          }
        },
        { xAxis: g.gapEnd }
      ])
    })

    const markLineLower = []
    // 1. 告警开始纵线（客观告警触发时刻）
    if (alarmStartTimeText.value) {
      markLineLower.push({
        xAxis: alarmStartTimeText.value,
        label: {
          formatter: `▲ 告警开始 (${alarmStartTimeText.value.slice(11, 19)})`,
          position: 'insideEndBottom',
          color: '#ff4d4f',
          fontSize: 10,
          fontWeight: 'bold',
          backgroundColor: '#fff1f0',
          borderColor: '#ffa39e',
          borderWidth: 1,
          borderRadius: 3,
          padding: [2, 5]
        },
        lineStyle: {
          color: '#ff4d4f',
          type: 'dashed',
          width: 1.8
        }
      })
    }

    // 2. 最近一次触发纵线（若与开始或结束时刻不同）
    if (alarmLastTimeText.value && alarmLastTimeText.value !== alarmStartTimeText.value && alarmLastTimeText.value !== alarmEndTimeText.value) {
      markLineLower.push({
        xAxis: alarmLastTimeText.value,
        label: {
          formatter: `▲ 最近触发 (${alarmLastTimeText.value.slice(11, 19)})`,
          position: 'insideEndBottom',
          color: '#fa8c16',
          fontSize: 10,
          fontWeight: 'bold',
          backgroundColor: '#fffbe6',
          borderColor: '#ffe58f',
          borderWidth: 1,
          borderRadius: 3,
          padding: [2, 4]
        },
        lineStyle: {
          color: '#fa8c16',
          type: 'dotted',
          width: 1.4
        }
      })
    }

    // 3. 告警结束纵线（已恢复时刻）
    if (alarmEndTimeText.value) {
      markLineLower.push({
        xAxis: alarmEndTimeText.value,
        label: {
          formatter: `▲ 告警结束 (${alarmEndTimeText.value.slice(11, 19)})`,
          position: 'insideEndBottom',
          color: '#059669',
          fontSize: 10,
          fontWeight: 'bold',
          backgroundColor: '#ecfdf5',
          borderColor: '#a7f3d0',
          borderWidth: 1,
          borderRadius: 3,
          padding: [2, 5]
        },
        lineStyle: {
          color: '#10b981',
          type: 'dashed',
          width: 1.8
        }
      })
    }

    const socSeriesData = data.map(d => [d.time, d.soc])
    const powerSeriesData = data.map(d => [d.time, d.power])

    chartInstance = echarts.init(dom)

    const yAxisConfig = isMeterDevice.value
      ? [
        {
          gridIndex: 0,
          type: 'value',
          name: '有功功率 (kW)',
          scale: true,
          nameLocation: 'end',
          nameTextStyle: { color: '#1D77F5', fontSize: 11, align: 'left', padding: [0, 0, 6, -48] },
          axisLabel: { color: '#8c8c8c', fontSize: 10, formatter: '{value}' },
          splitLine: { show: true, lineStyle: { color: '#f5f5f5', type: 'dashed' } }
        },
        {
          gridIndex: 1,
          type: 'value',
          name: '无功功率 (kvar)',
          scale: true,
          nameLocation: 'end',
          nameTextStyle: { color: '#FFC859', fontSize: 11, align: 'left', padding: [0, 0, 6, -48] },
          axisLabel: { color: '#8c8c8c', fontSize: 10, formatter: '{value}' },
          splitLine: { show: true, lineStyle: { color: '#f5f5f5', type: 'dashed' } }
        },
        {
          gridIndex: 1,
          type: 'value',
          name: '功率因数 (PF)',
          min: -1,
          max: 1,
          position: 'right',
          nameLocation: 'end',
          nameTextStyle: { color: '#00D4AA', fontSize: 11, align: 'right', padding: [0, 0, 6, 36] },
          axisLabel: { color: '#8c8c8c', fontSize: 10, formatter: '{value}' },
          splitLine: { show: false }
        }
      ]
      : [
        {
          gridIndex: 0,
          type: 'value',
          name: isStationOffline.value ? '全站平均 SOC (%)' : '电池 SOC (%)',
          min: 0,
          max: 100,
          interval: 25,
          nameLocation: 'end',
          nameTextStyle: { color: '#2655f1', fontSize: 11, align: 'left', padding: [0, 0, 6, -48] },
          axisLabel: { color: '#8c8c8c', fontSize: 10, formatter: '{value}%' },
          splitLine: { show: true, lineStyle: { color: '#f5f5f5', type: 'dashed' } }
        },
        {
          gridIndex: 1,
          type: 'value',
          name: isStationOffline.value ? '全站总功率 (kW)' : '充放电功率 (kW)',
          scale: true,
          nameLocation: 'end',
          nameTextStyle: { color: '#00b96b', fontSize: 11, align: 'left', padding: [0, 0, 6, -48] },
          axisLabel: { color: '#8c8c8c', fontSize: 10, formatter: '{value}' },
          splitLine: { show: true, lineStyle: { color: '#f5f5f5', type: 'dashed' } }
        }
      ]

    const seriesConfig = isMeterDevice.value
      ? [
        {
          name: '有功功率',
          type: 'line',
          xAxisIndex: 0,
          yAxisIndex: 0,
          showSymbol: false,
          smooth: true,
          itemStyle: { color: '#1D77F5' },
          lineStyle: { width: 2, color: '#1D77F5' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(29, 119, 245, 0.35)' },
              { offset: 1, color: 'rgba(29, 119, 245, 0.02)' }
            ])
          },
          data: powerSeriesData,
          markArea: markAreaUpper.length ? { data: markAreaUpper } : undefined
        },
        {
          name: '无功功率',
          type: 'line',
          xAxisIndex: 1,
          yAxisIndex: 1,
          showSymbol: false,
          smooth: true,
          itemStyle: { color: '#FFC859' },
          lineStyle: { width: 1.6, color: '#FFC859' },
          data: data.map(d => [d.time, d.qTotal]),
          markArea: markAreaLower.length ? { data: markAreaLower, label: { show: false } } : undefined,
          markLine: markLineLower.length ? { data: markLineLower, symbol: ['none', 'none'] } : undefined
        },
        {
          name: '功率因数',
          type: 'line',
          xAxisIndex: 1,
          yAxisIndex: 2,
          showSymbol: false,
          smooth: true,
          itemStyle: { color: '#00D4AA' },
          lineStyle: { width: 1.4, color: '#00D4AA' },
          data: data.map(d => [d.time, d.pf])
        }
      ]
      : [
        {
          name: isStationOffline.value ? '全站平均 SOC' : '电池 SOC',
          type: 'line',
          xAxisIndex: 0,
          yAxisIndex: 0,
          showSymbol: false,
          smooth: true,
          itemStyle: { color: '#2655f1' },
          lineStyle: { width: 2, color: '#2655f1' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(38, 85, 241, 0.35)' },
              { offset: 1, color: 'rgba(38, 85, 241, 0.02)' }
            ])
          },
          data: socSeriesData,
          markArea: markAreaUpper.length ? { data: markAreaUpper } : undefined
        },
        {
          name: isStationOffline.value ? '全站总功率' : '充放电功率',
          type: 'line',
          xAxisIndex: 1,
          yAxisIndex: 1,
          showSymbol: false,
          smooth: true,
          itemStyle: { color: '#00b96b' },
          lineStyle: { width: 1.6, color: '#00b96b' },
          data: powerSeriesData,
          markArea: markAreaLower.length ? { data: markAreaLower, label: { show: false } } : undefined,
          markLine: markLineLower.length ? { data: markLineLower, symbol: ['none', 'none'] } : undefined
        }
      ]

    const option = {
      animation: false,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          lineStyle: { color: '#8c8c8c', type: 'dashed' }
        },
        formatter: function (params) {
          if (!params || !params.length) return ''
          let timeTitle = ''
          params.forEach(p => {
            if (p.data && p.data[0]) timeTitle = p.data[0]
          })
          if (!timeTitle && params[0].axisValue) timeTitle = params[0].axisValue
          let res = `<div style="font-weight:600;margin-bottom:5px;color:#262626;font-size:12px;border-bottom:1px solid #f0f0f0;padding-bottom:3px;">采样时刻: ${timeTitle}</div>`
          params.forEach(p => {
            const val = p.data && p.data[1] !== undefined && p.data[1] !== null ? p.data[1] : '--'
            let unit = ' kW'
            if (p.seriesName.includes('SOC')) unit = '%'
            else if (p.seriesName.includes('因数')) unit = ''
            else if (p.seriesName.includes('无功')) unit = ' kvar'
            res += `<div style="display:flex;justify-content:space-between;gap:18px;font-size:12px;margin-top:3px;">
            <span style="color:#595959;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px;"></span>${p.seriesName}</span>
            <span style="font-weight:600;color:#262626;">${val}${unit}</span>
          </div>`
          })
          return res
        }
      },
      axisPointer: {
        link: [{ xAxisIndex: 'all' }]
      },
      grid: [
        { top: 26, left: 62, right: isMeterDevice.value ? 54 : 24, height: '34%', containLabel: false },
        { top: '56%', left: 62, right: isMeterDevice.value ? 54 : 24, bottom: 38, containLabel: false }
      ],
      xAxis: [
        {
          gridIndex: 0,
          type: 'time',
          min: props.powerTrackingStart || undefined,
          max: endTargetStr || undefined,
          boundaryGap: false,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          splitLine: { show: true, lineStyle: { color: '#f5f5f5', type: 'dashed' } }
        },
        {
          gridIndex: 1,
          type: 'time',
          min: props.powerTrackingStart || undefined,
          max: endTargetStr || undefined,
          boundaryGap: false,
          axisLine: { lineStyle: { color: '#d9d9d9' } },
          axisTick: { show: true },
          axisLabel: { color: '#8c8c8c', fontSize: 11, formatter: '{HH}:{mm}' },
          splitLine: { show: true, lineStyle: { color: '#f5f5f5', type: 'dashed' } }
        }
      ],
      yAxis: yAxisConfig,
      dataZoom: [
        { type: 'inside', xAxisIndex: [0, 1] },
        {
          type: 'slider',
          xAxisIndex: [0, 1],
          height: 12,
          bottom: 4,
          borderColor: '#e8e8e8',
          fillerColor: 'rgba(38, 85, 241, 0.12)',
          handleSize: '100%',
          handleStyle: { color: '#2655f1' },
          textStyle: { color: '#8c8c8c', fontSize: 10 }
        }
      ],
      series: seriesConfig
    }

    chartInstance.setOption(option)
  } catch (err) {
    console.warn('[OfflineTelemetryChartCard] renderChart error:', err)
  }
}

function handleResize() {
  chartInstance?.resize()
}

watch(
  () => [props.records, props.powerTrackingStart, props.powerTrackingEnd],
  () => {
    nextTick(renderChart)
  },
  { deep: true }
)

onMounted(() => {
  nextTick(renderChart)
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})
</script>

<style scoped lang="scss">
.evidence-box {
  border-radius: 10px;
  padding: 14px 16px;
  border: 1px solid #f1f5f9;
  background: #f8fafc;

  .evidence-box-title {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;

    .evidence-status-tag {
      font-size: 10px;
      padding: 1px 6px;
      border-radius: 4px;
      margin-left: 6px;
      font-weight: 600;
      background: #eff6ff;
      color: #2655f1;
    }
  }

  .section-sub-title {
    font-size: 14px !important;
    font-weight: 700 !important;
    color: #1e293b !important;
    line-height: 1.4;
  }

  .evidence-box-desc {
    font-size: 14px;
    line-height: 1.65;
    color: #334155;
    margin: 0;
  }

  &.evidence-telemetry {
    background: transparent;
    border: none;
    padding: 0;
    box-shadow: none;

    .evidence-box-title {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .title-left-wrap {
        display: flex;
        align-items: center;
      }

      /* 右上角高层级定性：克制典雅的纯文本排版（状态加粗，颜色区分） */
      .telemetry-headline-status {
        display: inline-flex;
        align-items: center;
        line-height: 1.4;

        .status-title {
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.2px;

          &.text-danger {
            color: #dc2626;
          }

          &.text-success {
            color: #16a34a;
          }
        }

        .status-divider {
          color: #cbd5e1;
          font-size: 12px;
          margin: 0 7px;
        }

        .status-meta {
          font-size: 12px;
          color: #64748b;
          font-weight: 400;
        }
      }
    }

    .telemetry-chart-wrapper {
      margin-top: 10px;
      background: #ffffff;
      border: 1px solid rgba(0, 0, 0, 0.06);
      border-radius: 6px;
      padding: 12px 14px 8px;

      /* 图表专属指标通栏 (Data Metric Ribbon)：label统一浅色，value统一深色，均不加粗 */
      .telemetry-metric-ribbon {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
        background: #f8fafc;
        border-radius: 6px;
        padding: 7px 12px;
        line-height: 1.4;

        .ribbon-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;

          .ribbon-label {
            color: #64748b;
            font-weight: 400;
          }

          .ribbon-val {
            color: #0f172a;
            font-weight: 400;
          }

          .ribbon-sub {
            color: #64748b;
            font-size: 12px;
            font-weight: 400;
            margin-left: 2px;
          }
        }

        .ribbon-divider {
          width: 1px;
          height: 12px;
          background: #cbd5e1;
          flex-shrink: 0;
        }
      }

      .chart-top-legend {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 18px;
        padding: 6px 4px 6px;
        border-bottom: 1px dashed #f0f0f0;

        .legend-indicator {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 11px;
          color: #595959;

          .legend-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;

            &.dot-soc {
              background: #2655f1;
            }

            &.dot-power {
              background: #00b96b;
            }
          }

          .legend-bar {
            width: 14px;
            height: 8px;
            border-radius: 2px;

            &.bar-gap {
              background: rgba(255, 77, 79, 0.35);
              border: 1px dashed #ff4d4f;
            }
          }

          .legend-label {
            line-height: 1;
          }
        }
      }

      .telemetry-chart-canvas {
        width: 100%;
        height: 380px;
      }

      .chart-bottom-hint {
        display: flex;
        align-items: center;
        padding: 4px 4px 0;
        font-size: 11px;
        color: #8c8c8c;
        line-height: 1.4;
      }
    }
  }
}
</style>
